// src/components/admin/CohortManagement.js - Admin cohort management interface
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import CohortDetail from './CohortDetail';
import './Admin.css';

const CohortManagement = ({ onBack }) => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const [cohorts, setCohorts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);

  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    if (userProfile && userProfile.tenant) {
      loadCohorts();
    }
  }, [userProfile]);

  const loadCohorts = async () => {
    if (!userProfile?.tenant?.id) return;

    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${userProfile.tenant.id}/cohorts`, user),
        'Loading cohorts...'
      );

      if (data.success) {
        setCohorts(data.data.cohorts || []);
      }
    } catch (err) {
      console.error('Error loading cohorts:', err);
    }
  };

  const createCohort = async (cohortData) => {
    if (!userProfile?.tenant?.id) return;

    try {
      const data = await makeRequest(
        () => apiService.post(`/api/v1/tenants/${userProfile.tenant.id}/cohorts`, cohortData, user),
        'Creating cohort...'
      );

      if (data.success) {
        setShowCreateModal(false);
        await loadCohorts();
        return data;
      }
    } catch (err) {
      console.error('Error creating cohort:', err);
      throw err;
    }
  };

  const archiveCohort = async (cohortId) => {
    if (!userProfile?.tenant?.id) return;

    try {
      const data = await makeRequest(
        () => apiService.delete(`/api/v1/tenants/${userProfile.tenant.id}/cohorts/${cohortId}`, user),
        'Archiving cohort...'
      );

      if (data.success) {
        setConfirmArchive(null);
        await loadCohorts();
      }
    } catch (err) {
      console.error('Error archiving cohort:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (selectedCohort) {
    return (
      <CohortDetail
        tenantId={userProfile.tenant.id}
        cohort={selectedCohort}
        onBack={() => {
          setSelectedCohort(null);
          loadCohorts();
        }}
      />
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="flex flex-between mb-6">
        <div>
          <button onClick={onBack} className="btn btn-secondary btn-sm mb-3">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Org Management
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">Cohort Management</h2>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
          disabled={loading}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Cohort
        </button>
      </div>

      {/* Loading/Error States */}
      {loading ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading cohorts...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <div>
            <strong>Error loading cohorts:</strong> {error}
          </div>
          <button onClick={loadCohorts} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      ) : cohorts.length === 0 ? (
        <div className="empty-state card">
          <div className="icon-container gray">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
            </svg>
          </div>
          <h3>No cohorts yet</h3>
          <p>Create a cohort to group participants who went through a class, workshop, or training seminar together.</p>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
            Create First Cohort
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {cohorts.map(cohort => (
            <div key={cohort.id} className="user-card">
              <div className="flex flex-between">
                <div className="flex-1">
                  <div className="flex gap-3 items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">{cohort.name}</h3>
                    <span className="status-pill" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                      {cohort.memberCount} {cohort.memberCount === 1 ? 'member' : 'members'}
                    </span>
                  </div>

                  {cohort.description && (
                    <p className="text-sm text-gray-600 mb-2">{cohort.description}</p>
                  )}

                  <div className="flex gap-4 text-sm text-gray-500">
                    {cohort.createdBy && (
                      <span>Created by {cohort.createdBy.name || cohort.createdBy.email}</span>
                    )}
                    <span>Created: {formatDate(cohort.createdAt)}</span>
                  </div>
                </div>

                <div className="user-actions">
                  <button
                    onClick={() => setSelectedCohort(cohort)}
                    className="btn btn-secondary btn-sm"
                    disabled={loading}
                  >
                    Manage
                  </button>
                  <button
                    onClick={() => setConfirmArchive(cohort)}
                    className="btn btn-sm"
                    style={{ backgroundColor: '#dc2626', color: 'white' }}
                    disabled={loading}
                  >
                    Archive
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Cohort Modal */}
      {showCreateModal && (
        <CreateCohortModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={createCohort}
          loading={loading}
        />
      )}

      {/* Archive Confirmation Modal */}
      {confirmArchive && (
        <ConfirmArchiveModal
          cohort={confirmArchive}
          onClose={() => setConfirmArchive(null)}
          onConfirm={() => archiveCohort(confirmArchive.id)}
          loading={loading}
        />
      )}
    </div>
  );
};

// Create Cohort Modal Component
const CreateCohortModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Cohort name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined
      });
    } catch (err) {
      console.error('Error submitting cohort:', err);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Create Cohort</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label" htmlFor="name">Cohort Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
              placeholder="Spring 2026 Leadership Cohort"
              disabled={loading}
              required
            />
            {formErrors.name && <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>}
          </div>

          <div className="mb-6">
            <label className="form-label" htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              rows={3}
              placeholder="Everyone who attended the March workshop"
              disabled={loading}
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1" disabled={loading || !formData.name.trim()}>
              {loading ? 'Creating...' : 'Create Cohort'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Archive Confirmation Modal Component
const ConfirmArchiveModal = ({ cohort, onClose, onConfirm, loading }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-header">
        <h3 className="modal-title">Archive Cohort</h3>
        <button onClick={onClose} className="modal-close" disabled={loading}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-1">
        Are you sure you want to archive <strong>{cohort.name}</strong>?
      </p>
      <p className="text-xs text-gray-500 mb-6">
        It will no longer appear in the active cohort list. Membership history is preserved and the cohort can be reactivated later.
      </p>

      <div className="modal-footer">
        <button onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="btn btn-primary flex-1"
          disabled={loading}
          style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
        >
          {loading ? 'Archiving...' : 'Archive'}
        </button>
      </div>
    </div>
  </div>
);

CohortManagement.propTypes = {
  onBack: PropTypes.func.isRequired
};

CreateCohortModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

ConfirmArchiveModal.propTypes = {
  cohort: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

export default CohortManagement;
